import { useCallback, useEffect, useRef, useState } from 'react';
import { EDITING } from '@geoic/shared';
import { ApiError } from '@/lib/api-error.js';
import type { ContentApi, ContentRecord } from './content-api.js';

export type LockHolder = { userId: string | null; userName: string; expiresAt: string | null };

export type EditLockState = {
  /** `mine` — qulf bizda; `taken` — boshqa xodimda; `none` — hali so'ralmagan. */
  status: 'none' | 'mine' | 'taken';
  holder: LockHolder | null;
  /** «Baribir tahrirlash» — qulfni tortib oladi (faqat admin/superadmin). */
  takeOver: () => Promise<void>;
  /** Ogohlantirishni yopish — qulfsiz davom etadi. */
  dismiss: () => void;
};

/**
 * Sahifa qulfi (TZ 7.2 §9).
 *
 * Bu OGOHLANTIRISH mexanizmi, blokirovka emas — server ham saqlashni
 * to'xtatmaydi. Ikkinchi muharrir xabarni ko'radi va o'zi qaror qiladi;
 * uning 20 daqiqalik ishini yo'q qilib yuborish qulfning vazifasi emas.
 *
 * Qulf har 45 soniyada yangilanadi (TTL 120 s). Brauzer yopilsa
 * heartbeat to'xtaydi va qulf o'z-o'zidan bo'shaydi.
 */
export function useEditLock(
  contentApi: ContentApi<ContentRecord>,
  id: string | null,
): EditLockState {
  const [status, setStatus] = useState<EditLockState['status']>('none');
  const [holder, setHolder] = useState<LockHolder | null>(null);
  const dismissed = useRef(false);

  const acquire = useCallback(
    async (force: boolean) => {
      if (!id) return;
      try {
        const lock = await contentApi.lock(id, force);
        setStatus('mine');
        setHolder({ userId: lock.userId, userName: lock.userName, expiresAt: lock.expiresAt });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'CONTENT_LOCKED') {
          const details = error.details as Partial<LockHolder> | undefined;
          if (!dismissed.current) setStatus('taken');
          setHolder({
            userId: details?.userId ?? null,
            userName: details?.userName ?? 'boshqa xodim',
            expiresAt: details?.expiresAt ?? null,
          });
          return;
        }
        // Tarmoq xatosi — qulf ishlamasa ham tahrirlash davom etaveradi.
        setStatus('none');
      }
    },
    [contentApi, id],
  );

  useEffect(() => {
    if (!id) return;
    dismissed.current = false;
    void acquire(false);

    const timer = setInterval(() => void acquire(false), EDITING.LOCK_HEARTBEAT_MS);

    return () => {
      clearInterval(timer);
      // Sahifadan chiqishda qulf darhol bo'shaydi — keyingi xodim
      // 2 daqiqa kutib o'tirmasin.
      void contentApi.unlock(id).catch(() => undefined);
    };
  }, [acquire, contentApi, id]);

  const takeOver = useCallback(async () => {
    dismissed.current = true;
    await acquire(true);
  }, [acquire]);

  const dismiss = useCallback(() => {
    dismissed.current = true;
    setStatus('none');
  }, []);

  return { status, holder, takeOver, dismiss };
}
